/**
 * Shared analytics types. Both client (emit) and server (ingest, aggregate)
 * agree on these shapes so the wire format stays a single source of truth.
 *
 * Storage portability: the server stores events in a Postgres table with
 * common dimensions pulled out as columns (mode, map_id, progress_tier,
 * ...) plus a JSONB `props` bag for everything else. The exact same shape
 * maps onto a ClickHouse `MergeTree(received_at)` table — flat columns
 * stay columns, `props` becomes `Map(String, String)` — so a future
 * migration is a pure schema move, not a rewrite of every emit site.
 */

import type { GameMode } from './chart.js';
import type { CirclesVariant } from './gameTypes.js';
import { MAX_CUPS, MAX_STARS } from './progression.js';

/** Every event type the system understands. New types must be added here AND on the server validator. */
export const ANALYTICS_EVENT_TYPES = [
  'session_open',
  'page_view',
  'song_start',
  'song_end',
  'song_abandon',
] as const;

export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number];

/**
 * Ordinal "how far did this run get" tier. Cleanly orderable so we can
 * take percentiles / build histograms with one column instead of a
 * (stars, cups) pair.
 *
 * - 0..3: stars earned in loop 1 (0 = died early, 3 = cleared loop 1)
 * - 4..6: cups earned in infinite-mode loops 2-3 (3 stars implied)
 */
export type ProgressTier = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const MAX_PROGRESS_TIER: ProgressTier = (3 + MAX_CUPS) as ProgressTier;

/**
 * Collapse (stars, cups) into a single ordinal tier 0..6. Cups dominate
 * stars: a 3-cup run is tier 6 regardless of how many stars were earned
 * along the way (you can only have cups if you cleared loop 1, which
 * already implies 3 stars).
 */
export function computeProgressTier(stars: number, cups: number): ProgressTier {
  const s = Math.max(0, Math.min(MAX_STARS, Math.floor(stars)));
  const c = Math.max(0, Math.min(MAX_CUPS, Math.floor(cups)));
  if (c > 0) {
    return (3 + c) as ProgressTier;
  }
  return s as ProgressTier;
}

/**
 * Human-readable label for a progress tier, useful for dashboards / tooltips.
 *
 * Labels are the bare star/cup count — the same vocabulary the player
 * sees on the results screen — so an admin reading a tier value
 * doesn't have to translate "Mastered" / "Cleared" back into the
 * underlying progression. 0..3 are stars, 4..6 are cups (which already
 * imply 3 stars + clearing loop 1).
 *
 * The `default` branch covers a future schema drift (e.g. a 7th tier
 * added before this switch is updated) so the dashboard renders the
 * raw integer rather than `undefined`. The `analytics_events` schema
 * stores `progress_tier` as an unconstrained integer column, so a
 * corrupt row from a misbehaving client could otherwise leak through.
 */
export function progressTierLabel(tier: ProgressTier): string {
  switch (tier) {
    case 0:
      return '0 stars';
    case 1:
      return '1 star';
    case 2:
      return '2 stars';
    case 3:
      return '3 stars';
    case 4:
      return '1 cup';
    case 5:
      return '2 cups';
    case 6:
      return '3 cups';
    default:
      return `tier ${tier as number}`;
  }
}

/**
 * Common dimensions extracted from every event for indexed aggregation.
 * Most are nullable because they don't apply to every event type (a
 * `session_open` has no map context).
 */
export interface AnalyticsEventDims {
  mode?: GameMode;
  mapId?: string;
  mapSlug?: string;
  difficulty?: number;
  variant?: CirclesVariant;
  progressTier?: ProgressTier;
  completed?: boolean;
  durationMs?: number;
}

/**
 * Shape of one event as it travels the wire. `eventId` is generated
 * client-side and used for idempotency — the server rejects duplicate
 * inserts via a UNIQUE constraint without raising an error to the
 * caller.
 */
export interface AnalyticsEventPayload extends AnalyticsEventDims {
  /** UUID v4 generated by the client. Must be unique across all events. */
  eventId: string;
  eventType: AnalyticsEventType;
  /** Client-side wall clock at emit time (ISO 8601). Server attaches its own `received_at`. */
  occurredAt: string;
  /**
   * Free-form structured payload for everything not promoted to a column.
   * Keys are snake_case to align with how they'd appear in a SQL `props->>`
   * lookup or a ClickHouse `Map` access; values stay JSON-primitive so
   * the JSONB column doesn't accidentally smuggle in objects we'd need
   * to re-parse later.
   */
  props?: Record<string, string | number | boolean | null>;
}

/** Top-level shape of POST /api/sessions/events. */
export interface AnalyticsBatchRequest {
  events: AnalyticsEventPayload[];
}

/** Maximum events per batch. Keeps payload size bounded and one bad client from flooding the table. */
export const ANALYTICS_MAX_BATCH_SIZE = 50;

/** Hard cap on `props` size after JSON.stringify, to keep one event from blowing the body limit. */
export const ANALYTICS_MAX_PROPS_BYTES = 4096;
